import React, { createContext, useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// Create the AuthContext
export const AuthContext = createContext();

// AuthProvider Component
export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  // On first mount, decide whether the persisted token is still a live session.
  // sessionStorage is cleared by the browser when it fully closes, so if the
  // token is present in localStorage but the "session_active" marker is gone,
  // the browser was closed and reopened -> treat it as expired and force login.
  const [token, setToken] = useState(() => {
    const storedToken = localStorage.getItem("auth_token") || "";
    const sessionActive = sessionStorage.getItem("session_active");
    if (storedToken && !sessionActive) {
      localStorage.removeItem("auth_token");
      return "";
    }
    return storedToken;
  });
  const [isAuthenticated, setAuthenticated] = useState(!!token); // Initial authentication state

  // Function to verify the token
  const verifyUserToken = async () => {
    try {
      // If the token exists and the path is "/", redirect to dashboard
      if (token && window.location.pathname === "/") {
        console.log("Token found. Redirecting to dashboard.");
        setAuthenticated(true);
        navigate("/dashboard");
        return;
      }

      // If no token and the path is "/", clear the token and set authentication to false
      if (!token && window.location.pathname === "/") {
        localStorage.removeItem("auth_token");
        setAuthenticated(false);
        return;
      }

      // If no token, clear the token, set authentication to false, and log the message
      if (!token) {
        localStorage.removeItem("auth_token");
        console.log(
          window.location.pathname + "---No token found. Redirecting to login."
        );
        setAuthenticated(false);
        navigate("/login");
        return;
      }

    } catch (error) {
      console.error("Error verifying token:", error);
      setAuthenticated(false);
      localStorage.removeItem("auth_token");
      navigate("/login");
    }
  };

  // Logout function
  const logout = async () => {
    try {
      // Call the backend logout route
      await fetch('/auth/logout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // ...other headers such as auth token if needed...
        },
        credentials: 'include'
      });
    } catch (error) {
      console.error('Logout failed:', error);
    }
    // Perform local logout
    setToken("");
    setAuthenticated(false);
    localStorage.removeItem("auth_token");
    sessionStorage.removeItem("session_active");
    navigate("/login");
  };

  useEffect(() => {
    verifyUserToken();
  }, [token]);

  return (
    <AuthContext.Provider value={{ token, setToken, isAuthenticated, setAuthenticated, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use the AuthContext
export const useAuth = () => useContext(AuthContext);
